import * as React from 'react';

import { DeliveryPersonDTO, Permission, Res } from 'core/api';
import { Result, SessionProvider, ValidationErrors } from 'core/infrastructure';
import { Content, Header, PosScreen } from 'pos/ui';
import * as Images from 'pos/Images';
import * as UI from 'core/ui';
import { CashdrawerFacade, OrderingFacade } from 'pos/facades';
import { ExternalPaymentsService } from 'pos/services';
import { Colors } from 'core/ui';
import { PaymentDTOFactory } from 'pos/modules/helpers/payments-processing';
import { Message, Modal } from 'core/ui-next/modal';
import { VStack, Box, Text, Image } from '@chakra-ui/react';
import { BasicButton, SvgIcon } from 'core/ui-next';
import { FormRef } from 'core/ui-next/controls/Form';
import { z } from 'zod';
import { zMonetary } from 'core/infrastructure-next';
import { useControlsFor } from 'core/ui-next/controls';
import { Currency } from 'core/utils';

export enum DeliveryPersonBalanceMovementType {
	Add, Substract
}

type Props = {
	deliveryPerson: DeliveryPersonDTO,
	type: DeliveryPersonBalanceMovementType
} & Modal.Actions<void>;

const stateSchema = z.object({
	amount: zMonetary({ nonNegative: true, required: true }),
	reason: z.string().optional(),
	registerCashTransaction: z.boolean()
});

type State = z.infer<typeof stateSchema>;

let registerCashInputWasPreviouslyChecked = false;

async function registerTransaction(deliveryPerson: DeliveryPersonDTO, deltaAmount: number, reason: string): Promise<Result> {

	// Si estoy quitando saldo al repartidor, tengo que introducirlo en el cajón (forPay).
	// Si le estoy añadiendo saldo, tengo que sacarlo(forRefund).
	const externalPaymentService = deltaAmount < 0
		? ExternalPaymentsService.forPay('NoDocument', null)
		: ExternalPaymentsService.forRefund('NoDocument', null);

	// El pago debe ser en valor absoluto, porque al distinguir entre forPay y forRefund, el ExternalPaymentService espera que el amount sea
	// siempre positivo si va en la dirección indicada (forPay -> introducir dinero, forRefund -> sacar dinero).
	const payment = PaymentDTOFactory.createCash(Math.abs(deltaAmount));
	const result = await externalPaymentService.register(payment);
	if (!result.ok)
		return Result.error();

	const cashControlResult = await externalPaymentService.commitCashPayments(0);
	if (!cashControlResult.ok)
		return Result.error();

	let details = Res.Msg.MSG_DELIVERY_PERSON_BALANCE_DELTA_DESCRIPTION(deliveryPerson.UserName);
	details = reason === '' ? details : `${details} (${reason})`;

	const registerTransactionResult = deltaAmount > 0
		? await CashdrawerFacade.registerOutputOperation(Res.Msg.MSG_DELIVERY_PERSON_BALANCE_PROVIDER(deliveryPerson.UserName), details, Math.abs(deltaAmount), true, false)
		: await CashdrawerFacade.registerInputOperation(Res.Msg.MSG_DELIVERY_PERSON_BALANCE_PROVIDER(deliveryPerson.UserName), details, Math.abs(deltaAmount), true, false);

	if (!registerTransactionResult.Printed)
		await Message.show(Res.Msg.MSG_REGISTER_CASH_TRANSACTION_PRINT_ERROR());

	if (!registerTransactionResult.Registered) {
		await Message.show(Res.Msg.MSG_INVALID_TRANSACTION_BECAUSE_POS_AMOUNT_BECOMES_NEGATIVE(UI.Format.asMoney(deltaAmount)));
		await externalPaymentService.undoAll();
		return Result.error();
	}

	return Result.ok();
}

function AddDeliveryPersonBalanceDelta(props: Props) {

	const { deliveryPerson, type, cancel, close } = props;

	// Cálculo dinámico del máximo de caracteres permitidos para el reason
	// Formato guardado en BD: "Caja {UserName} ({reason})"
	// Restricción de BD: CashTransaction.Description = 255 caracteres máximo
	const MAX_DESCRIPTION_LENGTH = 255;  // Longitud máxima de Description en CashTransaction

	const FIXED_PREFIX = Res.Msg.MSG_DELIVERY_PERSON_BALANCE_DELTA_DESCRIPTION(deliveryPerson.UserName).length; // En español - "Caja {UserName}" = longitud dinámica
	const FIXED_BRACKETS = 3; // " (" + ")" = 3 caracteres

	const maxReasonLength = MAX_DESCRIPTION_LENGTH - FIXED_PREFIX - FIXED_BRACKETS;

	const onAccept = async () => {
		const valid = await formRef.current!.getValue();

		if (!valid.ok)
			return;

		const value = valid.value;

		const amount = type === DeliveryPersonBalanceMovementType.Add ? value.amount : -value.amount;
		if (valid) {

			if (value.registerCashTransaction && SessionProvider.getCurrent().MasterHasCashControl) {
				await Message.show(Res.Msg.MSG_INVALID_SLAVE_CASH_OPERATION());
				return;
			}

			const result = value.registerCashTransaction ? await registerTransaction(deliveryPerson, amount, value.reason ?? '') : Result.ok();
			if (!result.ok)
				return;

			await OrderingFacade.addDeliveryPersonBalanceDelta(deliveryPerson.UserId, amount, value.reason ?? '');
			registerCashInputWasPreviouslyChecked = initialState.registerCashTransaction;

			close();
		}
	};

	const validate = React.useCallback(async (data: State) => {
		const errors: ValidationErrors = {};

		if (type === DeliveryPersonBalanceMovementType.Add && data.amount === 0)
			ValidationErrors.add(errors, 'amount', Res.Msg.MSG_CANNOT_ADD_ZERO_DELIVERY_PERSON_BALANCE());

		if (type === DeliveryPersonBalanceMovementType.Substract && data.amount > deliveryPerson.Balance)
			ValidationErrors.add(errors, 'amount', Res.Msg.MSG_CANNOT_SUBSTRACT_MORE_THAN_PENDING_DELIVERY_PERSON_BALANCE());

		if (type === DeliveryPersonBalanceMovementType.Add && data.amount > 9999)
			ValidationErrors.add(errors, 'amount', Res.Msg.MSG_CANNOT_ADD_MORE_THAN_MAX_BALANCE());

		return errors;
	}, [type, deliveryPerson.Balance]);

	const formRef = React.useRef<FormRef<State>>(null);
	const { Form, DecimalInput, TextArea, CheckBox } = useControlsFor<State>();

	const initialState: State = {
		amount: props.type === DeliveryPersonBalanceMovementType.Add ? 0 : props.deliveryPerson.Balance,
		reason: '',
		registerCashTransaction: registerCashInputWasPreviouslyChecked
	};

	return <PosScreen size={'small'} screenId={'ADD_DELIVERY_PERSON_BALANCE'} useKeyPad={true}>
		< Header
			title={type === DeliveryPersonBalanceMovementType.Add
				? Res.Web.ADD_DELIVERY_PERSON_BALANCE_DELTA_ADD_MODE()
				: Res.Web.SUBSTRACT_DELIVERY_PERSON_BALANCE_DELTA_ADD_MODE()
			}
			image={type === DeliveryPersonBalanceMovementType.Add
				? Images.AddPayment
				: Images.RemovePayment
			}
			onClose={cancel} />
		<Content>
			<VStack padding={6} overflowY={'auto'} height={'full'}>
				<VStack gap={6} w={'full'}>
					<VStack gap={2} w={'full'}>
						<VStack gap={1}>
							<Box
								width={'12'}
								height={'12'}
								background={deliveryPerson.UserImageId === Images.NULL_IMAGE ? Colors.getBackgroundColorAsRgb(deliveryPerson.UserBackColor) : 'inherit'}>
								{
									deliveryPerson.UserImageId !== Images.NULL_IMAGE
										? <Image src={Images.imgSrcFromId(deliveryPerson.UserImageId)} borderRadius={'full'} />
										: <SvgIcon src={Images.User} borderRadius={'full'} />
								}
							</Box>
							<Text fontWeight={'semibold'} fontSize={'sm'} lineClamp={1}>{Res.Web.ADD_DELIVERY_PERSON_BALANCE_DELTA_DELIVERY_PERSON_LBL(deliveryPerson.UserName)}</Text>
						</VStack>
						<Text fontSize={'sm'} color={{ base: 'neutral.600', _dark: 'neutral.400' }}>
							{type === DeliveryPersonBalanceMovementType.Add
								? Res.Web.ADD_DELIVERY_PERSON_BALANCE_DELTA_PROVIDED_AMOUNT_LBL()
								: Res.Web.SUBSTRACT_DELIVERY_PERSON_BALANCE_DELTA_PENDING_AMOUNT_LBL()}
							{': ' + UI.Format.asMoney(deliveryPerson.Balance)}
						</Text>
					</VStack>
					<VStack w={'full'}>
						<Form ref={formRef} initialValue={initialState} schema={stateSchema} style={{ width: '100%' }} validate={validate}>
							<VStack w={'full'} gap={4}>
								<DecimalInput
									linkTo={'amount'}
									label={type === DeliveryPersonBalanceMovementType.Add
										? Res.Web.ADD_DELIVERY_PERSON_BALANCE_DELTA_BALANCE_LBL()
										: Res.Web.SUBSTRACT_DELIVERY_PERSON_BALANCE_DELTA_PROVIDED_AMOUNT_LBL()
									}
									placeholder={Res.Common.DECIMAL_INPUT_CURRENCY_PLACEHOLDER(Currency.SYMBOL)} />
								<TextArea
									linkTo={'reason'}
									placeholder={Res.Web.ADD_DELIVERY_PERSON_BALANCE_DELTA_REASON_PLACEHOLDER()}
									rows={1}
									maxLength={maxReasonLength}
									className={'disable-keypad'} />
								<CheckBox
									size={'sm'}
									linkTo={'registerCashTransaction'}
									label={Res.Web.ADD_DELIVERY_PERSON_BALANCE_DELTA_CASH_MOVEMENT_CHK()} cursor={'pointer'} />
							</VStack>
						</Form>
					</VStack>
					<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={() => onAccept()} />
				</VStack>
			</VStack>
		</Content>
	</PosScreen >;
}

async function canBeExecuted(type: DeliveryPersonBalanceMovementType) {

	if (!SessionProvider.getCurrent().AllowCashTransactions) {
		await Message.show(Res.Msg.MSG_CURRENT_POS_DOES_NOT_ALLOW_CASH_TRANSACTIONS());
		return false;
	}

	const requiredPermission = type === DeliveryPersonBalanceMovementType.Add
		? Permission.DeliveryPerson_AddBalance
		: Permission.DeliveryPerson_SubstractBalance;

	if (SessionProvider.getCurrent().Permissions.includes(requiredPermission))
		return true;

	await Message.show(Res.Msg.MSG_PERMISSION_DENIED_FOR(Permission.toString(requiredPermission)));
	return false;
}

export default async function addDeliveryPersonBalanceDelta(deliveryPerson: DeliveryPersonDTO, type: DeliveryPersonBalanceMovementType): Promise<Result> {
	if (! await canBeExecuted(type))
		return Result.error();

	return Modal.show(AddDeliveryPersonBalanceDelta, { deliveryPerson: deliveryPerson, type: type });
}